import authService from './auth.service';
import type { BrandingConfig, BrandingActivity, BrandingActivityPage } from '../types/branding.types';

const API_URL = '/api/v1';
const CACHE_KEY_PREFIX = 'ab3-branding-';

// --- Public API (no auth) ---

async function resolve(params: { domain?: string; slug?: string }): Promise<BrandingConfig | null> {
  const url = new URL(`${API_URL}/branding/resolve`, window.location.origin);
  if (params.domain) url.searchParams.set('domain', params.domain);
  if (params.slug) url.searchParams.set('slug', params.slug);

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 3000);

  try {
    const res = await fetch(url.pathname + url.search, { signal: controller.signal });
    if (res.status === 404) return null;
    if (!res.ok) throw new Error(`Branding resolve failed: ${res.status}`);
    return (await res.json()) as BrandingConfig;
  } catch (err: any) {
    if (err.name === 'AbortError') {
      console.warn('[branding] resolve timeout');
    } else {
      console.warn('[branding] resolve error:', err.message);
    }
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

// --- Auth-required API ---

async function getConfig(orgId: number): Promise<BrandingConfig | null> {
  const res = await authService.fetchWithAuth(`${API_URL}/branding/${orgId}`);
  if (res.status === 404) return null;
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new Error(err?.detail || 'Не удалось загрузить конфиг брендинга');
  }
  return (await res.json()) as BrandingConfig;
}

// --- Admin API ---

export interface BrandingSavePayload {
  app_name?: string;
  primary_color?: string;
  accent_color?: string | null;
  custom_domain?: string | null;
  show_powered_by?: boolean;
  logo_url?: string | null;
  favicon_url?: string | null;
}

async function saveConfig(orgId: number, config: BrandingSavePayload): Promise<BrandingConfig> {
  const res = await authService.fetchWithAuth(`${API_URL}/admin/branding/${orgId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(config),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new Error(err?.detail || 'Не удалось сохранить конфиг брендинга');
  }
  return (await res.json()) as BrandingConfig;
}

async function uploadLogo(orgId: number, file: File): Promise<{ logo_url: string }> {
  const formData = new FormData();
  formData.append('file', file);

  const res = await authService.fetchWithAuth(`${API_URL}/admin/branding/${orgId}/logo`, {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new Error(err?.detail || 'Не удалось загрузить логотип');
  }
  return (await res.json()) as { logo_url: string };
}

async function uploadFavicon(orgId: number, file: File): Promise<{ favicon_url: string }> {
  const formData = new FormData();
  formData.append('file', file);

  const res = await authService.fetchWithAuth(`${API_URL}/admin/branding/${orgId}/favicon`, {
    method: 'POST',
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new Error(err?.detail || 'Не удалось загрузить favicon');
  }
  return (await res.json()) as { favicon_url: string };
}

async function deleteConfig(orgId: number): Promise<void> {
  const res = await authService.fetchWithAuth(`${API_URL}/admin/branding/${orgId}`, {
    method: 'DELETE',
  });
  if (!res.ok && res.status !== 404) {
    const err = await res.json().catch(() => null);
    throw new Error(err?.detail || 'Не удалось сбросить брендинг');
  }
}

async function deleteLogo(orgId: number): Promise<void> {
  const res = await authService.fetchWithAuth(`${API_URL}/admin/branding/${orgId}/logo`, {
    method: 'DELETE',
  });
  if (!res.ok && res.status !== 404) {
    const err = await res.json().catch(() => null);
    throw new Error(err?.detail || 'Не удалось удалить логотип');
  }
}

async function deleteFavicon(orgId: number): Promise<void> {
  const res = await authService.fetchWithAuth(`${API_URL}/admin/branding/${orgId}/favicon`, {
    method: 'DELETE',
  });
  if (!res.ok && res.status !== 404) {
    const err = await res.json().catch(() => null);
    throw new Error(err?.detail || 'Не удалось удалить favicon');
  }
}

async function generatePreviewToken(orgId: number): Promise<{ token: string; expires_at: string }> {
  const res = await authService.fetchWithAuth(`${API_URL}/admin/branding/preview-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ org_id: orgId }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new Error(err?.detail || 'Не удалось создать preview-токен');
  }
  return await res.json();
}

async function getActivity(
  orgId: number,
  params: { limit?: number; offset?: number } = {},
): Promise<BrandingActivityPage> {
  const limit = params.limit ?? 50;
  const offset = params.offset ?? 0;
  const search = new URLSearchParams({ limit: String(limit), offset: String(offset) });
  const res = await authService.fetchWithAuth(
    `${API_URL}/admin/branding/${orgId}/activity?${search}`,
  );
  if (res.status === 404) {
    return { items: [], total: 0, limit, offset };
  }
  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new Error(err?.detail || 'Не удалось загрузить историю изменений');
  }
  const data = await res.json();
  if (Array.isArray(data)) {
    return { items: data as BrandingActivity[], total: data.length, limit, offset };
  }
  return data as BrandingActivityPage;
}

async function exportConfig(orgId: number): Promise<BrandingConfig> {
  const config = await getConfig(orgId);
  if (!config) throw new Error('Конфиг не найден');
  return config;
}

async function importConfig(orgId: number, config: Partial<BrandingConfig>): Promise<BrandingConfig> {
  return saveConfig(orgId, config);
}

// --- localStorage cache ---

function getCached(orgId: number): { config: BrandingConfig; version: number } | null {
  try {
    const raw = localStorage.getItem(`${CACHE_KEY_PREFIX}${orgId}`);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function setCache(config: BrandingConfig): void {
  try {
    localStorage.setItem(`${CACHE_KEY_PREFIX}${config.org_id}`, JSON.stringify({
      config,
      version: config.version,
    }));
  } catch {
    // localStorage full or unavailable — ignore
  }
}

function clearCache(): void {
  try {
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key?.startsWith(CACHE_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // ignore
  }
}

function getCachedBySlug(): { config: BrandingConfig; version: number } | null {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(CACHE_KEY_PREFIX)) {
        const raw = localStorage.getItem(key);
        if (raw) {
          const parsed = JSON.parse(raw);
          return parsed;
        }
      }
    }
    return null;
  } catch {
    return null;
  }
}

const brandingService = {
  resolve,
  getConfig,
  saveConfig,
  uploadLogo,
  uploadFavicon,
  deleteConfig,
  deleteLogo,
  deleteFavicon,
  generatePreviewToken,
  getActivity,
  exportConfig,
  importConfig,
  getCached,
  setCache,
  clearCache,
  getCachedBySlug,
};

export default brandingService;
